// =============================================================================
// Iter 139 — ClientPicker (combobox)
// =============================================================================
// Dropdown + typeahead for the Log Time dialog on `Timesheet`. Backed by
// `GET /api/clients/picker` which returns EVERY active client (minimal
// fields only — client_id + name + kind), ignoring the user's team-scope.
// This lets a preparer log one-off / ad-hoc work for a client they
// aren't on the team for, without an admin having to toggle their
// "Sees all clients (override)" flag.
//
// The first row is always the synthetic "Internal / Admin" bucket so
// staff can book admin/firm-ops time against a client_id reports can
// group on.

import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, Briefcase, User as UserIcon, Building2 } from "lucide-react";

// Project-wide constant — kept in sync with server.py `INTERNAL_CLIENT_ID`.
const INTERNAL_ID = "__internal__";

function kindGlyph(kind) {
  if (kind === "internal")   return <Briefcase className="h-3.5 w-3.5 text-amber-600"/>;
  if (kind === "individual") return <UserIcon className="h-3.5 w-3.5 text-slate-500"/>;
  return <Building2 className="h-3.5 w-3.5 text-slate-500"/>;
}

export default function ClientPicker({
  value,                         // selected client_id or ""
  onChange,                      // (client_id, name) => void
  placeholder = "No client",
  allowClear = true,
  testIdPrefix = "client-picker",
}) {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/clients/picker")
      .then((r) => setClients(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const selected = useMemo(
    () => clients.find((c) => c.client_id === value) || null,
    [clients, value]
  );

  const label = selected ? selected.name : placeholder;
  const isInternal = selected?.client_id === INTERNAL_ID;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between font-normal ${selected ? "text-slate-900" : "text-slate-500"}`}
          data-testid={`${testIdPrefix}-trigger`}
        >
          <span className="flex items-center gap-2 truncate">
            {selected && kindGlyph(selected.kind)}
            <span className="truncate">{label}</span>
            {isInternal && (
              <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded px-1">
                admin
              </span>
            )}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400 shrink-0 opacity-60"/>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]" align="start">
        <Command loop filter={(itemValue, search) => {
          // Case-insensitive substring match over the item's cmdk `value`
          // (we embed the name there). Rank 1 = match, 0 = hide.
          return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
        }}>
          <CommandInput
            placeholder="Type a client name…"
            data-testid={`${testIdPrefix}-search`}
          />
          <CommandList>
            <CommandEmpty>
              {loading ? "Loading clients…" : "No matching client."}
            </CommandEmpty>
            {allowClear && (
              <CommandGroup>
                <CommandItem
                  value="__no_client__"
                  onSelect={() => { onChange("", ""); setOpen(false); }}
                  data-testid={`${testIdPrefix}-clear`}
                >
                  <span className="text-slate-500 italic">— {placeholder} —</span>
                  {!value && <Check className="h-3.5 w-3.5 ml-auto text-slate-900"/>}
                </CommandItem>
              </CommandGroup>
            )}
            {/* Pin the Internal / Admin row at the top as its own group so
                it never gets lost in the alphabetical list. */}
            {clients.some((c) => c.client_id === INTERNAL_ID) && (
              <CommandGroup heading="Firm ops">
                {clients.filter((c) => c.client_id === INTERNAL_ID).map((c) => (
                  <CommandItem
                    key={c.client_id}
                    value={c.name}
                    onSelect={() => { onChange(c.client_id, c.name); setOpen(false); }}
                    data-testid={`${testIdPrefix}-item-${c.client_id}`}
                  >
                    {kindGlyph(c.kind)}
                    <span className="ml-2 flex-1">{c.name}</span>
                    <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded px-1 mr-1">admin</span>
                    {value === c.client_id && <Check className="h-3.5 w-3.5 text-slate-900"/>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            <CommandGroup heading="Clients">
              {clients.filter((c) => c.client_id !== INTERNAL_ID).map((c) => (
                <CommandItem
                  key={c.client_id}
                  value={c.name}
                  onSelect={() => { onChange(c.client_id, c.name); setOpen(false); }}
                  data-testid={`${testIdPrefix}-item-${c.client_id}`}
                >
                  {kindGlyph(c.kind)}
                  <span className="ml-2 flex-1 truncate">{c.name}</span>
                  {c.kind === "individual" && (
                    <span className="text-[9px] uppercase tracking-wider text-slate-500 mr-1">personal</span>
                  )}
                  {value === c.client_id && <Check className="h-3.5 w-3.5 text-slate-900"/>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { INTERNAL_ID };
